import { memo, useCallback, useEffect, useState } from 'react';
import { Add, Remove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import { parseQtyInput, qtyStep, roundQty } from '../util/quantity';
import { focusAndSelect, selectOnFocus } from '../util/focusField';

/** Count input of the same column in the row above/below (DataGrid rows carry data-rowindex). */
const neighbourInput = (input: HTMLInputElement, delta: number): HTMLInputElement | null => {
  const row = input.closest<HTMLElement>('[data-rowindex]');
  const field = input.closest<HTMLElement>('[data-field]')?.dataset.field;
  const grid = input.closest<HTMLElement>('.MuiDataGrid-root');
  if (!row || !field || !grid) return null;
  const next = Number(row.dataset.rowindex) + delta;
  return grid.querySelector<HTMLInputElement>(`[data-rowindex="${next}"] [data-field="${field}"] input`);
};

type Props = {
  rowId: string;
  value: number;
  min: number;
  max: number;
  fractional?: boolean;
  onQuantityChangeByRowId: (rowId: string, value: number) => void;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const stepperButtonSx = {
  position: 'absolute',
  zIndex: 1,
  opacity: 0,
  width: 20,
  height: 20,
  p: 0,
  transition: 'opacity 150ms ease',
  bgcolor: 'background.paper',
  boxShadow: 1,
  '.qty-stepper-root:hover &, .qty-stepper-root:focus-within &, &:focus-visible': {
    opacity: 1,
  },
  '&:hover': {
    opacity: 1,
  },
} as const;

const QuantityGridCell = ({
  rowId,
  value,
  min,
  max,
  fractional = false,
  onQuantityChangeByRowId,
}: Props) => {
  const { t } = useTranslation();
  const step = qtyStep(fractional);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = useCallback((next: number) => {
    const clamped = clamp(roundQty(next), min, max);
    if (clamped !== value) {
      onQuantityChangeByRowId(rowId, clamped);
    }
  }, [max, min, onQuantityChangeByRowId, rowId, value]);

  const applyDelta = useCallback((delta: number) => {
    commit(value + delta);
  }, [commit, value]);

  return (
    <Box
      className="qty-stepper-root"
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box
        component="input"
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const parsed = parseQtyInput(draft, fractional);
          if (parsed != null) {
            commit(parsed);
          } else {
            setDraft(String(value));
          }
        }}
        autoComplete="off"
        onFocus={selectOnFocus}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            // Enter commits and moves to the next row's count (Shift+Enter: previous row).
            event.preventDefault();
            event.stopPropagation();
            const input = event.currentTarget;
            const next = neighbourInput(input, event.shiftKey ? -1 : 1);
            input.blur();
            if (next) focusAndSelect(next);
          }
        }}
        sx={{
          width: '100%',
          height: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 'inherit',
          fontFamily: 'inherit',
          color: 'inherit',
          MozAppearance: 'textfield',
          px: 3,
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />

      <IconButton
        size="small"
        tabIndex={-1}
        aria-label={t('common:ui.aria.decreaseQuantity')}
        disabled={value <= min}
        onClick={(event) => {
          event.stopPropagation();
          applyDelta(-step);
        }}
        sx={{ ...stepperButtonSx, left: 2, bottom: 4 }}
      >
        <Remove sx={{ fontSize: 14 }} />
      </IconButton>

      <IconButton
        size="small"
        tabIndex={-1}
        aria-label={t('common:ui.aria.increaseQuantity')}
        disabled={value >= max}
        onClick={(event) => {
          event.stopPropagation();
          applyDelta(+step);
        }}
        sx={{ ...stepperButtonSx, right: 2, bottom: 4 }}
      >
        <Add sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

export default memo(
  QuantityGridCell,
  (prev, next) =>
    prev.rowId === next.rowId
    && prev.value === next.value
    && prev.min === next.min
    && prev.max === next.max
    && prev.fractional === next.fractional
    && prev.onQuantityChangeByRowId === next.onQuantityChangeByRowId,
);